import React from "react";

export type SortDir = "asc" | "desc";
export type SortState = { colId: string; dir: SortDir } | null;

export const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

/**
 * Generic value comparison for table sorting. Rows come off the API loosely
 * typed (numbers arrive as strings, dates as ISO strings), so numeric-looking
 * strings are compared as numbers and everything else falls back to a
 * natural-order string compare. Nulls sort first.
 */
function compareValues(a: unknown, b: unknown): number {
  const aEmpty = a == null || a === "" || Number.isNaN(a);
  const bEmpty = b == null || b === "" || Number.isNaN(b);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return -1;
  if (bEmpty) return 1;

  if (typeof a === "number" && typeof b === "number") return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();

  const as = String(a);
  const bs = String(b);
  const an = Number(as);
  const bn = Number(bs);
  if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;

  return as.localeCompare(bs, undefined, { numeric: true, sensitivity: "base" });
}

export type TableView<T> = {
  /** The slice of rows to render for the current page. */
  pageRows: T[];
  page: number;
  totalPages: number;
  pageSize: number;
  setPageSize: (size: number) => void;
  /** 1-based index of the first row on this page; 0 when there are no rows. */
  from: number;
  to: number;
  total: number;
  sort: SortState;
  toggleSort: (colId: string) => void;
  goPrev: () => void;
  goNext: () => void;
  goToPage: (page: number) => void;
};

/**
 * Client-side paging + sorting over an already-fetched row array.
 *
 * The list endpoints return whole collections with no paging params, so paging
 * happens here rather than server-side. Keeping it in one hook means every list
 * screen behaves identically and the page-size selector actually does something.
 */
export function useTableView<T extends Record<string, unknown>>(
  rows: T[],
  options?: {
    initialPageSize?: number;
    initialSort?: SortState;
    /**
     * Column id -> row key to sort that column by instead of its displayed value.
     * For formatted cells such as dates: the row carries the display string under
     * the column id and a raw timestamp/ISO value under the mapped key.
     */
    sortKeys?: Record<string, string>;
  }
): TableView<T> {
  const [pageSize, setPageSizeState] = React.useState(
    options?.initialPageSize ?? 10
  );
  const [page, setPage] = React.useState(1);
  const [sort, setSort] = React.useState<SortState>(options?.initialSort ?? null);

  const sortKey = sort ? options?.sortKeys?.[sort.colId] ?? sort.colId : null;
  const sortedRows = React.useMemo(() => {
    if (!sort || !sortKey) return rows;
    const factor = sort.dir === "asc" ? 1 : -1;
    // Sort a copy so the caller's array (React state) is never mutated.
    return [...rows].sort(
      (a, b) => compareValues(a[sortKey], b[sortKey]) * factor
    );
  }, [rows, sort, sortKey]);

  const total = sortedRows.length;
  const totalPages = total === 0 ? 0 : Math.ceil(total / pageSize);

  // Deleting the last row on the final page (or shrinking the page size) can
  // strand the view past the end, so clamp instead of rendering an empty page.
  const safePage = totalPages === 0 ? 1 : Math.min(page, totalPages);
  React.useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const startIndex = (safePage - 1) * pageSize;
  const pageRows = React.useMemo(
    () => sortedRows.slice(startIndex, startIndex + pageSize),
    [sortedRows, startIndex, pageSize]
  );

  const setPageSize = React.useCallback((size: number) => {
    setPageSizeState(size);
    setPage(1);
  }, []);

  const toggleSort = React.useCallback((colId: string) => {
    setSort((current) => {
      if (!current || current.colId !== colId) return { colId, dir: "asc" };
      if (current.dir === "asc") return { colId, dir: "desc" };
      return null; // third click clears the sort and restores API order
    });
    setPage(1);
  }, []);

  const goPrev = React.useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const goNext = React.useCallback(
    () => setPage((p) => (totalPages === 0 ? 1 : Math.min(totalPages, p + 1))),
    [totalPages]
  );
  const goToPage = React.useCallback(
    (next: number) =>
      setPage(() =>
        totalPages === 0 ? 1 : Math.min(Math.max(1, next), totalPages)
      ),
    [totalPages]
  );

  return {
    pageRows,
    page: safePage,
    totalPages,
    pageSize,
    setPageSize,
    from: total === 0 ? 0 : startIndex + 1,
    to: Math.min(startIndex + pageSize, total),
    total,
    sort,
    toggleSort,
    goPrev,
    goNext,
    goToPage,
  };
}
